import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { TenantContext } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { useSession } from '../auth/SessionProvider';
import { toTenantContext, type MembershipRow } from './resolve';
import { loadPausedWorkspaces, type PausedWorkspace } from '../lifecycle/lifecycle';

/**
 * Which companies this person belongs to.
 *
 * Read through Row Level Security with the anon key, so the list is not
 * something the client filters — it is everything the database is willing to
 * hand over, which is exactly their own memberships. A person who belongs to
 * nothing gets an empty array, not an error.
 *
 * The rows are dropped the moment the session changes. Tenant data surviving a
 * sign-out, or a switch between companies, is the failure this product must not
 * have, and the cheapest way to avoid it is never to hold it across one.
 */

export interface WorkspaceContextValue {
    contexts: TenantContext[];
    /**
     * Companies this person belongs to that are not in ordinary use — pending
     * deletion, being deleted, suspended. RLS hides them from every ordinary
     * read; this is how their members learn why, and an owner can cancel.
     */
    paused: PausedWorkspace[];
    loading: boolean;
    error: string | null;
    reload: () => Promise<void>;
    /** The slug this browser was last in, remembered only as a convenience. */
    lastUsedSlug: string | null;
    rememberSlug: (slug: string) => void;
}

const WorkspaceListContext = createContext<WorkspaceContextValue | null>(null);

const LAST_SLUG_KEY = 'timely.lastWorkspaceSlug';

const MEMBERSHIP_RECHECK_MS = 5 * 60_000;

const sameAs = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

const readLastSlug = (): string | null => {
    try {
        return window.localStorage.getItem(LAST_SLUG_KEY);
    } catch {
        // Private windows and blocked site data both throw here. It is a
        // convenience, so losing it is not an error.
        return null;
    }
};

/**
 * One query for everything the shell needs about a membership.
 *
 * `workspace_members` and `workspace_roles` are both readable by a member of
 * the same company, and by nobody else, so the join needs no filtering of its
 * own — and a filter here would be a client-side boundary, which is the thing
 * this architecture refuses to rely on.
 */
const MEMBERSHIP_QUERY = `
    member_id:id,
    workspace_id,
    user_id,
    role_id,
    display_name,
    code,
    status,
    role:workspace_roles!inner ( name, is_owner, capabilities ),
    workspace:workspaces!inner ( id, name, slug, logo_path, currency, locale, timezone, phone_country )
`;

interface RawRow {
    member_id: string;
    workspace_id: string;
    user_id: string;
    role_id: string;
    display_name: string | null;
    code: string | null;
    status: string;
    role: { name: string; is_owner: boolean; capabilities: string[] | null } | null;
    workspace: MembershipRow['workspace'] | null;
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const { userId, state, loading: sessionLoading } = useSession();
    const [contexts, setContexts] = useState<TenantContext[]>([]);
    const [paused, setPaused] = useState<PausedWorkspace[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUsedSlug, setLastUsedSlug] = useState<string | null>(readLastSlug);

    /**
     * `quiet` is the background re-check: no spinner, nothing torn down, and a
     * failure changes nothing — the next check tries again. Only an answer
     * that differs from what the screen holds is applied.
     */
    const load = useCallback(async (quiet = false) => {
        // Until the session has settled we do not know whether this person has
        // companies, and "we do not know" must not be reported as "none". The
        // guard turns "none" into a redirect to Create your company, so
        // answering early sends somebody who has a company to set one up.
        if (sessionLoading) {
            if (!quiet) setLoading(true);
            return;
        }
        if (!userId || state !== 'active') {
            setContexts([]);
            setPaused([]);
            setLoading(false);
            return;
        }
        if (!quiet) {
            setLoading(true);
            setError(null);
        }
        const [{ data, error: queryError }, pausedRows] = await Promise.all([
            supabase()
                .from('workspace_members')
                .select(MEMBERSHIP_QUERY)
                .eq('user_id', userId)
                .eq('status', 'active'),
            loadPausedWorkspaces(),
        ]);

        if (queryError) {
            if (quiet) return;
            setPaused(pausedRows);
            setContexts([]);
            setError('We could not load your companies. Try again in a moment.');
            setLoading(false);
            return;
        }
        setPaused(prev => sameAs(prev, pausedRows) ? prev : pausedRows);

        const rows = (data ?? []) as unknown as RawRow[];
        const next = rows
            .filter(r => r.role && r.workspace)
            .map(r => toTenantContext({
                member_id: r.member_id,
                workspace_id: r.workspace_id,
                user_id: r.user_id,
                role_id: r.role_id,
                role_name: r.role!.name,
                is_owner: r.role!.is_owner,
                capabilities: r.role!.capabilities,
                display_name: r.display_name,
                code: r.code,
                status: r.status,
                workspace: r.workspace!,
            }));
        setContexts(prev => sameAs(prev, next) ? prev : next);
        if (!quiet) setLoading(false);
    }, [userId, state, sessionLoading]);

    useEffect(() => { void load(); }, [load]);

    // What this person may do was read when the session began. An owner can
    // change a role, suspend a member or schedule deletion at any moment, and
    // the database already refuses everything that no longer holds — but a
    // screen that kept the old picture would go on showing a book that now
    // comes back empty, as if it were empty. So the list is asked again when
    // the tab comes back into view, and every few minutes while it is open;
    // a membership that has gone takes the person to "You do not have access",
    // and a changed role takes its new capabilities, without a reload.
    useEffect(() => {
        if (!userId || state !== 'active') return;
        const recheck = () => {
            if (document.visibilityState === 'visible') void load(true);
        };
        document.addEventListener('visibilitychange', recheck);
        window.addEventListener('focus', recheck);
        const timer = window.setInterval(recheck, MEMBERSHIP_RECHECK_MS);
        return () => {
            document.removeEventListener('visibilitychange', recheck);
            window.removeEventListener('focus', recheck);
            window.clearInterval(timer);
        };
    }, [load, userId, state]);

    const rememberSlug = useCallback((slug: string) => {
        setLastUsedSlug(slug);
        try {
            window.localStorage.setItem(LAST_SLUG_KEY, slug);
        } catch { /* a convenience, not a requirement */ }
    }, []);

    const reload = useCallback(() => load(), [load]);

    const value = useMemo<WorkspaceContextValue>(
        () => ({ contexts, paused, loading, error, reload, lastUsedSlug, rememberSlug }),
        [contexts, paused, loading, error, reload, lastUsedSlug, rememberSlug],
    );

    return <WorkspaceListContext.Provider value={value}>{children}</WorkspaceListContext.Provider>;
}

export function useWorkspaces(): WorkspaceContextValue {
    const value = useContext(WorkspaceListContext);
    if (!value) throw new Error('useWorkspaces must be used inside a WorkspaceProvider');
    return value;
}

/* -------------------------- the one being looked at ------------------------- */

const ActiveWorkspaceContext = createContext<TenantContext | null>(null);

export const ActiveWorkspaceProvider = ActiveWorkspaceContext.Provider;

/**
 * The company currently on screen.
 *
 * Deliberately never ambient: it is provided by the guarded `/w/:slug` layout
 * and nowhere else, so no screen can read "the current workspace" from a global
 * that might be stale after a switch.
 */
export function useActiveWorkspace(): TenantContext {
    const value = useContext(ActiveWorkspaceContext);
    if (!value) throw new Error('useActiveWorkspace must be used inside a workspace route');
    return value;
}
